import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Network, Download, X, ExternalLink, Crosshair, ArrowLeftRight, ArrowRight, FileText } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useOrgId } from "../hooks/useOrgId";
import { useAutoSyncRefresh } from "../hooks/useAutoSyncRefresh";
import * as api from "../lib/api";
import { GraphCanvas, useSelection, RadialMenu, lightTheme } from "reagraph";
import CmsGate from "../components/CmsGate";
import SearchDemandGraph from "../components/graph/SearchDemandGraph";

const PRIMARY = "#018D4C";

const CLUSTER_COLORS = [
  "#018D4C", "#FF2A00", "#FF8C00", "#9B59B6", "#1ABC9C",
  "#E67E22", "#3498DB", "#E74C3C", "#2ECC71", "#8E44AD",
  "#16A085", "#D35400", "#2980B9", "#27AE60", "#0055FF",
];

const GRADE_COLOR = { A: "#22C55E", B: "#3B82F6", C: "#F59E0B", D: "#EF4444" };
const INTERNAL_LINK_COLOR = "#22C55E";
const OPPORTUNITY_COLOR = "#F97316";

const THEME = {
  ...lightTheme,
  canvas: { background: "#ffffff", fog: "#ffffff" },
  node: { ...lightTheme.node, inactiveOpacity: 0.12 },
  edge: { ...lightTheme.edge, inactiveOpacity: 0.06, opacity: 0.7 },
};

const MODES = [
  { key: "search", label: "Search demand", title: "Search topics → the pages that serve them → links. Works without a CMS" },
  { key: "content", label: "Content", title: "Synced CMS articles grouped by content cluster" },
];

// Two maps of topical authority: search demand (GSC + site crawl, no CMS needed, default)
// and content (synced articles, CMS only).
export default function KnowledgeGraph() {
  const [mode, setMode] = useState(() => {
    try { return localStorage.getItem("kg:mode") || "search"; } catch { return "search"; }
  });
  const pick = (m) => { setMode(m); try { localStorage.setItem("kg:mode", m); } catch {} };
  return (
    <div className="p-6 sm:p-8 max-w-7xl mx-auto">
      <div className="mb-6 pb-6 border-b border-zinc-100 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500 mb-1">Visualization</p>
          <h1 className="text-4xl font-bold text-zinc-900 tracking-tight">Topic Authority Map</h1>
          <p className="text-sm text-zinc-400 mt-1">
            {mode === "search"
              ? "What people search → which page answers it → how your pages link to the pages that convert"
              : "Hub size = authority · green = linked · orange = link opportunity"}
          </p>
        </div>
        <div className="flex gap-0.5 bg-zinc-100 rounded-lg p-0.5">
          {MODES.map(m => (
            <button key={m.key} onClick={() => pick(m.key)} title={m.title}
              className="px-3 py-1 text-xs font-bold rounded-md whitespace-nowrap"
              style={mode === m.key
                ? { background: "#fff", color: "#18181b", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }
                : { color: "#71717a" }}>
              {m.label}
            </button>
          ))}
        </div>
      </div>
      {mode === "search" ? (
        <SearchDemandGraph />
      ) : (
        <CmsGate feature="the content map" description="Map how your synced articles relate by topic, and spot clusters with thin coverage.">
          <ContentGraph />
        </CmsGate>
      )}
    </div>
  );
}

function ContentGraph() {
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [allEdges, setAllEdges] = useState([]);
  const [clusters, setClusters] = useState([]);
  const [showInternalLinks, setShowInternalLinks] = useState(true);
  const [showOpportunities, setShowOpportunities] = useState(true);
  const [loading, setLoading] = useState(true);
  const [selectedNode, setSelectedNode] = useState(null);
  const [pathSource, setPathSource] = useState(null);
  const [lassoStats, setLassoStats] = useState(null);
  const [glKey, setGlKey] = useState(0);
  const [contextLost, setContextLost] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  useAutoSyncRefresh(() => setRefreshKey(k => k + 1));
  const glRetries = useRef(0);
  const containerRef = useRef(null);
  const graphRef = useRef(null);
  const orgId = useOrgId();
  const navigate = useNavigate();

  const {
    selections,
    actives,
    selectNodePaths,
    clearSelections,
    onNodeClick,
    onCanvasClick,
    onNodePointerOver,
    onNodePointerOut,
    onLasso,
    onLassoEnd,
  } = useSelection({
    ref: graphRef,
    nodes,
    edges,
    type: "single",
    pathHoverType: "all",
    pathSelectionType: "all",
    focusOnSelect: false,
  });

  const nodeIdSet = useMemo(() => new Set(nodes.map(n => n.id)), [nodes]);
  const nodeById = useMemo(() => Object.fromEntries(nodes.map(n => [n.id, n])), [nodes]);

  const actionItems = useMemo(() => {
    const gaps = clusters
      .filter(c => c.authority_grade === "C" || c.authority_grade === "D")
      .sort((a, b) => (a.authority_score ?? 0) - (b.authority_score ?? 0))
      .slice(0, 5)
      .map(c => ({
        id: `gap:${c.cluster_id}`,
        nodeId: `cluster:${c.cluster_id}`,
        clusterId: c.cluster_id,
        label: c.label,
        grade: c.authority_grade,
        score: Math.round(c.authority_score ?? 0),
        size: c.size ?? 0,
      }));

    const opportunities = allEdges
      .filter(e => e.edgeType === "link_opportunity")
      .slice()
      .sort((a, b) => (b.data?.value ?? 0) - (a.data?.value ?? 0))
      .slice(0, 5)
      .map(e => ({
        id: e.id,
        source: e.source,
        target: e.target,
        sourceLabel: nodeById[e.source]?.label || e.source,
        targetLabel: nodeById[e.target]?.label || e.target,
        similarity: e.data?.value ?? 0,
      }));

    return { gaps, opportunities };
  }, [clusters, allEdges, nodeById]);

  const safeNodePointerOver = useCallback((data) => {
    if (!nodeIdSet.has(data.id)) return;
    onNodePointerOver(data);
  }, [nodeIdSet, onNodePointerOver]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const graphData = await api.getGraph();
        if (cancelled) return;

        const rawClusters = graphData.clusters || [];
        setClusters(rawClusters);

        const clusterColorMap = Object.fromEntries(
          rawClusters.map((c, i) => [c.cluster_id, CLUSTER_COLORS[i % CLUSTER_COLORS.length]])
        );
        const clusterLabelMap = Object.fromEntries(rawClusters.map(c => [c.cluster_id, c.label]));

        const reagraphNodes = (graphData.nodes || []).map(n => {
          const color = clusterColorMap[n.cluster_id] || PRIMARY;
          if (n.type === "cluster") {
            const score = n.authority_score || 0;
            return {
              id: n.id,
              label: n.title || n.cluster_id,
              fill: color,
              size: Math.max(14, Math.round((score / 100) * 28)),
              data: {
                type: "cluster",
                cluster_id: n.cluster_id,
                authority_score: n.authority_score,
                authority_grade: n.authority_grade,
                article_count: n.size,
              },
            };
          }
          return {
            id: n.id,
            label: n.title || n.id,
            fill: color,
            size: 5,
            data: {
              type: "article",
              cluster_id: n.cluster_id,
              cluster_label: clusterLabelMap[n.cluster_id] || n.cluster_id,
              wp_url: n.wp_url,
            },
          };
        });

        const reagraphEdges = (graphData.edges || []).map(e => {
          if (e.type === "spoke") {
            const clusterId = e.source.replace("cluster:", "");
            return {
              id: `${e.source}>${e.target}`,
              source: e.source,
              target: e.target,
              edgeType: "spoke",
              fill: clusterColorMap[clusterId] || PRIMARY,
              size: 1,
              opacity: 0.3,
              dashed: false,
              interpolation: "linear",
            };
          }
          if (e.type === "internal_link") {
            return {
              id: `il:${e.source}-${e.target}`,
              source: e.source,
              target: e.target,
              edgeType: "internal_link",
              fill: INTERNAL_LINK_COLOR,
              size: 2,
              dashed: false,
              interpolation: "curved",
            };
          }
          return {
            id: `opp:${e.source}-${e.target}`,
            source: e.source,
            target: e.target,
            edgeType: "link_opportunity",
            fill: OPPORTUNITY_COLOR,
            size: 1.5,
            dashed: true,
            interpolation: "curved",
            data: { value: e.value },
          };
        });

        setAllEdges(reagraphEdges);
        setNodes(reagraphNodes);
        setSelectedNode(null);
        setPathSource(null);
        setLassoStats(null);
      } catch (err) {
        console.error("[KnowledgeGraph] load error", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [orgId, refreshKey]);

  useEffect(() => {
    setEdges(allEdges.filter(e => {
      if (e.edgeType === "spoke") return true;
      if (e.edgeType === "internal_link") return showInternalLinks;
      if (e.edgeType === "link_opportunity") return showOpportunities;
      return true;
    }));
  }, [allEdges, showInternalLinks, showOpportunities]);

  useEffect(() => {
    if (nodes.length === 0 || loading) return;
    const t = setTimeout(() => { graphRef.current?.fitNodesInView(); }, 600);
    return () => clearTimeout(t);
  }, [nodes, loading]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const attachListeners = (c) => {
      if (c.__glHandled) return;
      c.__glHandled = true;
      c.addEventListener("webglcontextlost", (e) => {
        e.preventDefault();
        if (glRetries.current < 3) {
          glRetries.current += 1;
          setTimeout(() => setGlKey(k => k + 1), 600);
        } else {
          setContextLost(true);
        }
      }, false);
      c.addEventListener("webglcontextrestored", () => setContextLost(false), false);
    };

    const existing = container.querySelector("canvas");
    if (existing) { attachListeners(existing); return; }

    const observer = new MutationObserver(() => {
      const c = container.querySelector("canvas");
      if (c) { attachListeners(c); observer.disconnect(); }
    });
    observer.observe(container, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [glKey]);

  const handleNodeClick = useCallback((node) => {
    if (pathSource && node.data?.type === "article") {
      selectNodePaths(pathSource.id, node.id);
      setPathSource(null);
      return;
    }
    onNodeClick(node);
    setSelectedNode(node);
    setLassoStats(null);
  }, [pathSource, selectNodePaths, onNodeClick]);

  const handleCanvasClick = useCallback((e) => {
    onCanvasClick(e);
    setSelectedNode(null);
    setPathSource(null);
    setLassoStats(null);
  }, [onCanvasClick]);

  const handleLassoEnd = useCallback((selectionIds) => {
    const nodeOnlyIds = selectionIds.filter(id => nodeIdSet.has(id));
    onLassoEnd(nodeOnlyIds);
    if (selectionIds.length === 0) return;
    const selSet = new Set(selectionIds);
    const selArticles = nodes.filter(n => selSet.has(n.id) && n.data?.type === "article");
    const selClusters = nodes.filter(n => selSet.has(n.id) && n.data?.type === "cluster");
    const selLinks = edges.filter(
      e => e.edgeType === "internal_link" && selSet.has(e.source) && selSet.has(e.target)
    );
    const selOpps = edges.filter(
      e => e.edgeType === "link_opportunity" && selSet.has(e.source) && selSet.has(e.target)
    );
    setLassoStats({
      articleCount: selArticles.length,
      clusterCount: selClusters.length,
      linkCount: selLinks.length,
      oppCount: selOpps.length,
      clusters: selClusters.map(n => ({
        label: n.label,
        grade: n.data?.authority_grade,
        fill: n.fill,
      })),
    });
    setSelectedNode(null);
  }, [nodes, edges, nodeIdSet, onLassoEnd]);

  const handleExport = useCallback(() => {
    const url = graphRef.current?.exportCanvas();
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.download = "topic-authority-map.png";
    a.click();
  }, []);

  const focusNode = useCallback((nodeId) => {
    graphRef.current?.centerGraph([nodeId]);
    const n = nodeById[nodeId];
    clearSelections();
    setPathSource(null);
    setLassoStats(null);
    setSelectedNode(n || null);
  }, [nodeById, clearSelections]);

  const focusOpportunity = useCallback((source, target) => {
    setShowOpportunities(true);
    graphRef.current?.centerGraph([source, target]);
    selectNodePaths(source, target);
    setPathSource(null);
    setSelectedNode(null);
    setLassoStats(null);
  }, [selectNodePaths]);

  const viewClusterArticles = useCallback((clusterId) => {
    navigate(clusterId ? `/dashboard/articles?cluster=${encodeURIComponent(clusterId)}` : "/dashboard/articles");
  }, [navigate]);

  const closeSidebar = useCallback(() => {
    setSelectedNode(null);
    setLassoStats(null);
    clearSelections();
  }, [clearSelections]);

  const internalLinkCount = allEdges.filter(e => e.edgeType === "internal_link").length;
  const opportunityCount = allEdges.filter(e => e.edgeType === "link_opportunity").length;
  const articleCount = nodes.filter(n => n.data?.type === "article").length;
  const showSidebar = selectedNode || lassoStats;

  return (
    <div data-testid="knowledge-graph-page">
      {/* Controls */}
      <div className="mb-6 flex flex-wrap items-end justify-end gap-4">
        <div className="flex flex-wrap items-center gap-4">
          {pathSource && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg">
              <span className="text-xs font-bold text-blue-700">
                Path from: {pathSource.label?.slice(0, 24)}… — click target article
              </span>
              <button
                onClick={() => { setPathSource(null); clearSelections(); }}
                className="text-blue-400 hover:text-blue-700"
              >
                <X size={12} />
              </button>
            </div>
          )}
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-1.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showInternalLinks}
                onChange={e => setShowInternalLinks(e.target.checked)}
                className="w-3.5 h-3.5"
                style={{ accentColor: INTERNAL_LINK_COLOR }}
              />
              <span className="text-xs font-bold" style={{ color: INTERNAL_LINK_COLOR }}>Internal links</span>
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showOpportunities}
                onChange={e => setShowOpportunities(e.target.checked)}
                className="w-3.5 h-3.5"
                style={{ accentColor: OPPORTUNITY_COLOR }}
              />
              <span className="text-xs font-bold" style={{ color: OPPORTUNITY_COLOR }}>Link opportunities</span>
            </label>
          </div>
          <button
            onClick={handleExport}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-zinc-200 rounded-lg text-xs font-bold text-zinc-600 hover:bg-zinc-50 transition-colors"
          >
            <Download size={13} />
            Export PNG
          </button>
        </div>
      </div>

      {/* Action Items */}
      {!loading && nodes.length > 0 && (
        <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-xl border border-zinc-100 shadow-sm p-4">
            <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500 mb-3">
              To-Do: Coverage Gaps
            </p>
            {actionItems.gaps.length === 0 ? (
              <p className="text-xs text-zinc-400">No low-authority clusters — coverage looks solid.</p>
            ) : (
              <div className="flex flex-col gap-2">
                {actionItems.gaps.map(g => (
                  <div key={g.id} className="flex items-center gap-2.5 bg-zinc-50 rounded-lg p-2.5">
                    <span
                      className="text-[10px] font-bold px-1.5 py-0.5 rounded flex-shrink-0"
                      style={{ color: GRADE_COLOR[g.grade] || "#71717A", background: `${GRADE_COLOR[g.grade] || "#71717A"}18` }}
                    >
                      {g.grade}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-semibold text-zinc-800 truncate">{g.label}</p>
                      <p className="text-[10px] text-zinc-400">{g.size} articles · {g.score}/100 authority</p>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <button
                        onClick={() => focusNode(g.nodeId)}
                        title="Focus this cluster in the graph"
                        className="flex items-center gap-1 px-2 py-1 bg-white border border-zinc-200 rounded-md text-[10px] font-bold text-zinc-600 hover:bg-zinc-100 transition-colors"
                      >
                        <Crosshair size={11} />
                        Focus
                      </button>
                      <button
                        onClick={() => viewClusterArticles(g.clusterId)}
                        title="View this cluster's articles"
                        className="flex items-center gap-1 px-2 py-1 bg-white border border-zinc-200 rounded-md text-[10px] font-bold text-zinc-600 hover:bg-zinc-100 transition-colors"
                      >
                        <FileText size={11} />
                        Articles
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <button
              onClick={() => navigate("/dashboard/recommendations")}
              className="mt-3 flex items-center gap-1 text-[11px] font-bold text-zinc-500 hover:text-zinc-800 transition-colors"
            >
              View AI write suggestions <ArrowRight size={11} />
            </button>
          </div>

          <div className="bg-white rounded-xl border border-zinc-100 shadow-sm p-4">
            <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500 mb-3">
              To-Do: Add Internal Links
            </p>
            {actionItems.opportunities.length === 0 ? (
              <p className="text-xs text-zinc-400">No high-similarity unlinked pairs found.</p>
            ) : (
              <div className="flex flex-col gap-2">
                {actionItems.opportunities.map(o => (
                  <div key={o.id} className="flex items-center gap-2.5 bg-zinc-50 rounded-lg p-2.5">
                    <span
                      className="text-[10px] font-bold px-1.5 py-0.5 rounded flex-shrink-0"
                      style={{ color: OPPORTUNITY_COLOR, background: `${OPPORTUNITY_COLOR}18` }}
                    >
                      {Math.round(o.similarity * 100)}%
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-semibold text-zinc-800 truncate">{o.sourceLabel}</p>
                      <p className="text-[10px] text-zinc-400 truncate">→ {o.targetLabel}</p>
                    </div>
                    <button
                      onClick={() => focusOpportunity(o.source, o.target)}
                      title="Show this pair in the graph"
                      className="flex items-center gap-1 px-2 py-1 bg-white border border-zinc-200 rounded-md text-[10px] font-bold text-zinc-600 hover:bg-zinc-100 transition-colors flex-shrink-0"
                    >
                      <ArrowLeftRight size={11} />
                      Show
                    </button>
                  </div>
                ))}
              </div>
            )}
            <button
              onClick={() => navigate("/dashboard/linking")}
              className="mt-3 flex items-center gap-1 text-[11px] font-bold text-zinc-500 hover:text-zinc-800 transition-colors"
            >
              View all linking suggestions <ArrowRight size={11} />
            </button>
          </div>
        </div>
      )}

      {/* Graph + Sidebar */}
      <div className="flex gap-4">
        <div
          ref={containerRef}
          className="flex-1 bg-white rounded-xl border border-zinc-100 shadow-sm overflow-hidden relative"
          style={{ height: 580 }}
        >
          {contextLost && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-white z-10">
              <p className="text-sm text-zinc-500 mb-3">WebGL context lost</p>
              <button
                onClick={() => { glRetries.current = 0; setContextLost(false); setGlKey(k => k + 1); }}
                className="px-3 py-1.5 bg-zinc-900 text-white text-xs font-bold rounded-lg hover:bg-zinc-700 transition-colors"
              >
                Reload Graph
              </button>
            </div>
          )}
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="w-8 h-8 border-2 border-zinc-900 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                <p className="text-sm text-zinc-400">Building topic map…</p>
              </div>
            </div>
          ) : nodes.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full">
              <Network size={48} color="#CCC" />
              <p className="text-sm text-zinc-500 mt-4">No graph data. Sync articles first.</p>
            </div>
          ) : (
            <GraphCanvas
              key={glKey}
              ref={graphRef}
              nodes={nodes}
              edges={edges}
              selections={selections}
              actives={actives}
              animated
              onNodeClick={handleNodeClick}
              onCanvasClick={handleCanvasClick}
              onNodePointerOver={safeNodePointerOver}
              onNodePointerOut={onNodePointerOut}
              onLasso={onLasso}
              onLassoEnd={handleLassoEnd}
              layoutType="forceDirected2d"
              cameraMode="pan"
              draggable
              labelType="auto"
              lassoType="node"
              sizingType="none"
              minNodeSize={4}
              maxNodeSize={30}
              theme={THEME}
              contextMenu={({ data, onClose }) => (
                <RadialMenu
                  onClose={onClose}
                  items={[
                    ...(data.data?.type === "article" ? [
                      {
                        label: "Open live",
                        onClick: () => {
                          if (data.data?.wp_url) window.open(data.data.wp_url, "_blank");
                          onClose();
                        },
                      },
                      {
                        label: "Find Paths",
                        onClick: () => {
                          setPathSource(data);
                          clearSelections();
                          setSelectedNode(null);
                          onClose();
                        },
                      },
                    ] : []),
                    {
                      label: "Articles",
                      onClick: () => { viewClusterArticles(data.data?.cluster_id); onClose(); },
                    },
                  ]}
                />
              )}
            />
          )}
        </div>

        {/* Sidebar */}
        {showSidebar && (
          <div
            className="w-64 flex-shrink-0 bg-white rounded-xl border border-zinc-100 shadow-sm p-4 flex flex-col gap-3 overflow-y-auto"
            style={{ height: 580 }}
          >
            {selectedNode?.data?.type === "cluster" && (
              <>
                <div className="flex items-center justify-between">
                  <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Topic Cluster</p>
                  <button onClick={closeSidebar} className="text-zinc-300 hover:text-zinc-600"><X size={14} /></button>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: selectedNode.fill || PRIMARY }} />
                  <p className="text-sm font-semibold text-zinc-900 leading-snug">{selectedNode.label}</p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-zinc-50 rounded-lg p-2.5">
                    <p className="text-xl font-bold text-zinc-900">{selectedNode.data.article_count ?? "—"}</p>
                    <p className="text-[10px] text-zinc-500 uppercase tracking-wide mt-0.5">Articles</p>
                  </div>
                  <div className="bg-zinc-50 rounded-lg p-2.5">
                    <p
                      className="text-xl font-bold"
                      style={{ color: GRADE_COLOR[selectedNode.data.authority_grade] || "#71717A" }}
                    >
                      {selectedNode.data.authority_grade || "—"}
                    </p>
                    <p className="text-[10px] text-zinc-500 uppercase tracking-wide mt-0.5">Grade</p>
                  </div>
                  <div className="col-span-2 bg-zinc-50 rounded-lg p-2.5">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-[10px] text-zinc-500 uppercase tracking-wide">Authority</p>
                      <p className="text-xs font-bold text-zinc-900">{Math.round(selectedNode.data.authority_score ?? 0)}/100</p>
                    </div>
                    <div className="w-full bg-zinc-200 rounded-full h-1.5">
                      <div
                        className="h-1.5 rounded-full"
                        style={{
                          width: `${selectedNode.data.authority_score ?? 0}%`,
                          background: GRADE_COLOR[selectedNode.data.authority_grade] || PRIMARY,
                        }}
                      />
                    </div>
                  </div>
                </div>
                {(selectedNode.data.authority_grade === "C" || selectedNode.data.authority_grade === "D") && (
                  <div className="bg-amber-50 border border-amber-100 rounded-lg p-2.5">
                    <p className="text-xs font-bold text-amber-700 mb-0.5">Coverage gap</p>
                    <p className="text-[11px] text-amber-600">Add more articles to strengthen authority.</p>
                  </div>
                )}
                <div className="mt-auto pt-3 border-t border-zinc-100">
                  <button
                    onClick={() => viewClusterArticles(selectedNode.data.cluster_id)}
                    className="w-full px-3 py-2 border border-zinc-200 text-zinc-600 text-xs font-bold rounded-lg hover:bg-zinc-50 transition-colors"
                  >
                    View Cluster Articles
                  </button>
                </div>
              </>
            )}

            {selectedNode?.data?.type === "article" && (
              <>
                <div className="flex items-center justify-between">
                  <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Article</p>
                  <button onClick={closeSidebar} className="text-zinc-300 hover:text-zinc-600"><X size={14} /></button>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: selectedNode.fill || PRIMARY }} />
                  <p className="text-sm font-semibold text-zinc-900 leading-snug">{selectedNode.label}</p>
                </div>
                {selectedNode.data?.cluster_label && (
                  <p className="text-[11px] text-zinc-400">Topic: {selectedNode.data.cluster_label}</p>
                )}
                <div className="flex flex-col gap-2 mt-auto pt-3 border-t border-zinc-100">
                  {selectedNode.data?.wp_url && (
                    <a
                      href={selectedNode.data.wp_url}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center justify-center gap-1.5 px-3 py-2 bg-zinc-900 text-white text-xs font-bold rounded-lg hover:bg-zinc-700 transition-colors"
                    >
                      <ExternalLink size={12} />
                      Open live page
                    </a>
                  )}
                  <button
                    onClick={() => { setPathSource(selectedNode); clearSelections(); setSelectedNode(null); }}
                    className="px-3 py-2 border border-zinc-200 text-zinc-600 text-xs font-bold rounded-lg hover:bg-zinc-50 transition-colors"
                  >
                    Find Paths From This
                  </button>
                  <button
                    onClick={() => viewClusterArticles(selectedNode.data.cluster_id)}
                    className="px-3 py-2 border border-zinc-200 text-zinc-600 text-xs font-bold rounded-lg hover:bg-zinc-50 transition-colors"
                  >
                    View Cluster Articles
                  </button>
                </div>
              </>
            )}

            {lassoStats && (
              <>
                <div className="flex items-center justify-between">
                  <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Selection</p>
                  <button onClick={closeSidebar} className="text-zinc-300 hover:text-zinc-600"><X size={14} /></button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-zinc-50 rounded-lg p-2.5">
                    <p className="text-xl font-bold text-zinc-900">{lassoStats.articleCount}</p>
                    <p className="text-[10px] text-zinc-500 uppercase tracking-wide mt-0.5">Articles</p>
                  </div>
                  <div className="bg-zinc-50 rounded-lg p-2.5">
                    <p className="text-xl font-bold text-zinc-900">{lassoStats.clusterCount}</p>
                    <p className="text-[10px] text-zinc-500 uppercase tracking-wide mt-0.5">Topics</p>
                  </div>
                  {lassoStats.linkCount > 0 && (
                    <div className="col-span-2 rounded-lg p-2.5" style={{ background: "#F0FDF4", border: "1px solid #BBF7D0" }}>
                      <p className="text-xl font-bold" style={{ color: INTERNAL_LINK_COLOR }}>{lassoStats.linkCount}</p>
                      <p className="text-[10px] uppercase tracking-wide mt-0.5" style={{ color: INTERNAL_LINK_COLOR }}>Internal links</p>
                    </div>
                  )}
                  {lassoStats.oppCount > 0 && (
                    <div className="col-span-2 rounded-lg p-2.5" style={{ background: "#FFF7ED", border: "1px solid #FED7AA" }}>
                      <p className="text-xl font-bold" style={{ color: OPPORTUNITY_COLOR }}>{lassoStats.oppCount}</p>
                      <p className="text-[10px] uppercase tracking-wide mt-0.5" style={{ color: OPPORTUNITY_COLOR }}>Link opportunities</p>
                    </div>
                  )}
                </div>
                {lassoStats.clusters.length > 0 && (
                  <div>
                    <p className="text-[10px] text-zinc-400 uppercase tracking-wide mb-2">Topics in selection</p>
                    <div className="flex flex-col gap-1.5">
                      {lassoStats.clusters.map((c, i) => (
                        <div key={i} className="flex items-center gap-1.5">
                          <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: c.fill }} />
                          <span className="text-xs text-zinc-500 truncate flex-1">{c.label}</span>
                          {c.grade && (
                            <span
                              className="text-[10px] font-bold px-1 rounded"
                              style={{ color: GRADE_COLOR[c.grade] || "#71717A", background: `${GRADE_COLOR[c.grade] || "#71717A"}18` }}
                            >
                              {c.grade}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Legend */}
      {clusters.length > 0 && (
        <div className="mt-4 bg-white rounded-xl border border-zinc-100 p-4 shadow-sm">
          <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500 mb-3">Topic Clusters</p>
          <div className="flex flex-wrap gap-3 mb-3">
            {clusters.map((c, i) => (
              <div key={c.cluster_id} className="flex items-center gap-1.5">
                <div
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ background: CLUSTER_COLORS[i % CLUSTER_COLORS.length] }}
                />
                <span className="text-xs text-zinc-500">{c.label}</span>
                <span className="text-[10px] text-zinc-400">({c.size})</span>
                {c.authority_grade && (
                  <span
                    className="text-[9px] font-bold px-1 rounded leading-4"
                    style={{
                      color: GRADE_COLOR[c.authority_grade] || "#71717A",
                      background: `${GRADE_COLOR[c.authority_grade] || "#71717A"}18`,
                    }}
                  >
                    {c.authority_grade}
                  </span>
                )}
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-4 pt-2 border-t border-zinc-100">
            <div className="flex items-center gap-1.5">
              <div className="w-6 h-0.5" style={{ background: INTERNAL_LINK_COLOR }} />
              <span className="text-xs" style={{ color: INTERNAL_LINK_COLOR }}>Existing internal link</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-6 h-0.5" style={{ borderTop: `2px dashed ${OPPORTUNITY_COLOR}` }} />
              <span className="text-xs" style={{ color: OPPORTUNITY_COLOR }}>
                Link opportunity (≥70% similar, not yet linked)
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="mt-3 flex gap-4 text-xs text-zinc-400">
        <span data-testid="graph-node-count">{articleCount} articles</span>
        <span>{clusters.length} topics</span>
        <span data-testid="graph-edge-count" style={{ color: INTERNAL_LINK_COLOR }}>{internalLinkCount} internal links</span>
        <span style={{ color: OPPORTUNITY_COLOR }}>{opportunityCount} opportunities</span>
        <span className="ml-auto">Shift+drag to lasso · Right-click article for actions · Hub size = authority</span>
      </div>
    </div>
  );
}
